import type { ProfileAttributeFilter, PublishConfigTargetAudience } from '@brushfire/types'
import {
  canonicalizeTargetAudienceForStorage,
  readAiSuggestedFilters,
} from './audiencePublishRuntime'
import { coerceAudienceFilters } from '@/features/instruments/publish/target-audience/lib/deviceTypeFilter'
import type {
  AudienceFilters,
  DemographicPanelId,
} from '@/features/instruments/publish/target-audience/lib/audienceTypes'
import {
  activeFiltersWithoutUnappliedAi,
  coerceDemographicPanelIds,
  computeUserModifiedPanelIds,
} from '@/features/instruments/publish/target-audience/lib/audienceFilterLogic'

type TargetAudienceRaw = PublishConfigTargetAudience & { aiBaseline?: AudienceFilters }

function readAiSuggestedFromRaw(raw: TargetAudienceRaw): AudienceFilters | undefined {
  try {
    return readAiSuggestedFilters(raw)
  } catch {
    return raw.aiSuggestedFilters ?? raw.aiBaseline
  }
}

function activeFiltersFromRaw(raw: PublishConfigTargetAudience): AudienceFilters {
  return coerceAudienceFilters(raw.filters ?? {})
}

export function emptyPublishTargetAudience(): PublishConfigTargetAudience {
  return { filters: {}, filtersModified: false, userModifiedPanelIds: [] }
}

function resolveUserModifiedPanelIdsFromRaw(
  raw: TargetAudienceRaw,
  filters: AudienceFilters,
  normalizedSuggested: AudienceFilters | undefined,
  suggestedPanelIds: DemographicPanelId[],
): DemographicPanelId[] {
  if (raw.userModifiedPanelIds != null) {
    return coerceDemographicPanelIds(raw.userModifiedPanelIds)
  }
  if (normalizedSuggested && suggestedPanelIds.length) {
    return computeUserModifiedPanelIds(filters, normalizedSuggested, suggestedPanelIds)
  }
  return []
}

export function normalizePublishTargetAudience(
  raw: TargetAudienceRaw | undefined | null,
): PublishConfigTargetAudience {
  if (!raw) return emptyPublishTargetAudience()
  const aiSuggestedFilters = readAiSuggestedFromRaw(raw)
  const suggestedPanelIds = coerceDemographicPanelIds(raw.suggestedPanelIds)
  const filters = coerceAudienceFilters(raw.filters ?? {})
  const normalizedSuggested = aiSuggestedFilters
    ? coerceAudienceFilters(aiSuggestedFilters)
    : undefined
  const userModifiedPanelIds = resolveUserModifiedPanelIdsFromRaw(
    raw,
    filters,
    normalizedSuggested,
    suggestedPanelIds,
  )

  const count =
    typeof raw.count === 'number' && Number.isFinite(raw.count) && raw.count >= 0
      ? Math.floor(raw.count)
      : undefined

  return {
    filters,
    aiSuggestedFilters: normalizedSuggested,
    suggestedPanelIds: suggestedPanelIds.length ? suggestedPanelIds : undefined,
    userModifiedPanelIds,
    filtersModified: raw.filtersModified ?? false,
    aiSuggestionsApplied: raw.aiSuggestionsApplied === true ? true : undefined,
    count,
    profileAttributeFilters: Array.isArray(raw.profileAttributeFilters)
      ? raw.profileAttributeFilters
      : [],
  }
}

/** Active filters from draft — never auto-select unapplied AI suggestions (BFP-252). */
export function activeAudienceFiltersFromDraft(
  targetAudience: PublishConfigTargetAudience,
): AudienceFilters {
  return activeFiltersWithoutUnappliedAi({
    filters: activeFiltersFromRaw(targetAudience),
    filtersModified: targetAudience.filtersModified === true,
    aiSuggestedFilters: targetAudience.aiSuggestedFilters,
    aiSuggestionsApplied: targetAudience.aiSuggestionsApplied === true,
  })
}

export function hasMeaningfulTargetAudienceDraft(
  targetAudience: PublishConfigTargetAudience,
): boolean {
  const normalized = normalizePublishTargetAudience(targetAudience)
  return (
    normalized.filtersModified === true || Object.keys(normalized.filters).length > 0
  )
}

export function buildPublishTargetAudienceSnapshot(input: {
  filters: AudienceFilters
  aiSuggestedFilters: AudienceFilters
  suggestedPanelIds: PublishConfigTargetAudience['suggestedPanelIds']
  userModifiedPanelIds?: DemographicPanelId[]
  filtersModified?: boolean
  aiSuggestionsApplied?: boolean
  count?: number
  profileAttributeFilters?: ProfileAttributeFilter[]
}): PublishConfigTargetAudience {
  const filters = coerceAudienceFilters(input.filters)
  const aiSuggestedFilters = coerceAudienceFilters(input.aiSuggestedFilters)
  const suggestedPanelIds = coerceDemographicPanelIds(input.suggestedPanelIds)
  const userModifiedPanelIds =
    input.userModifiedPanelIds == null
      ? computeUserModifiedPanelIds(filters, aiSuggestedFilters, suggestedPanelIds)
      : coerceDemographicPanelIds(input.userModifiedPanelIds)

  const count =
    typeof input.count === 'number' && Number.isFinite(input.count) && input.count >= 0
      ? Math.floor(input.count)
      : undefined

  const filtersModified = input.filtersModified === true || userModifiedPanelIds.length > 0

  return canonicalizeTargetAudienceForStorage({
    filters: structuredClone(filters),
    aiSuggestedFilters: structuredClone(aiSuggestedFilters),
    suggestedPanelIds: suggestedPanelIds.length ? suggestedPanelIds : undefined,
    userModifiedPanelIds,
    filtersModified,
    aiSuggestionsApplied: input.aiSuggestionsApplied === true,
    count,
    profileAttributeFilters: input.profileAttributeFilters ?? [],
  })
}
